import React, { useCallback, useEffect, useState } from "react";
import "./checkpoint-exam-content.component.scoped.scss";
import { useTranslation } from "react-i18next";
import { services } from "../../../../../services";
import { Box, Typography } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import { BreadcrumbComponent } from "../../../../../components/breadcrumb/breadcrumb.component";
import { educationRoutes } from "../../../types/education.routes";
import { formatDate } from "../../../../../helpers";
import { icons } from "../../../../../statics/icons";
import ExamQuestionsComponent from "../exam-questions/exam-questions.component";
import CheckpointResultComponent from "../checkpoint-result/checkpoint-result.component";

const hasUserResult = (userResult: any): boolean =>
  Boolean(userResult && Object.keys(userResult).length > 0);

const CheckpointExamContentComponent = ({ examId }: { examId: string }) => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [examDetails, setExamDetails] = useState<any>({});
  const [userResult, setUserResult] = useState<any>(null);
  const [examRetakeKey, setExamRetakeKey] = useState(0);

  const fetchExamQuestions = useCallback(async () => {
    setLoading(true);
    try {
      const response = await services.courses.getCheckpointExam(examId);
      setExamDetails(response.checkpoint ?? {});
      setUserResult(response.userResult ?? null);
    } catch (error) {
      console.error("Error fetching checkpoint exam:", error);
    } finally {
      setLoading(false);
    }
  }, [examId]);

  useEffect(() => {
    fetchExamQuestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [examId]);

  const handleExamComplete = useCallback((result: any) => {
    setUserResult(result);
  }, []);

  const handleRepeat = useCallback(() => {
    setUserResult(null);
    setExamRetakeKey((prev) => prev + 1);
  }, []);

  const showResult = hasUserResult(userResult);

  return (
    <Box className="checkpoint__details__content pb-4">
      {loading ? (
        <Skeleton variant="text" width={400} height={18} className="my-4" />
      ) : (
        <BreadcrumbComponent
          items={[
            {
              label: t("EDUCATION_PAGE.EDUCATION"),
              path: educationRoutes.courses,
              separator: "-",
            },
            {
              label: examDetails.course?.title,
              path: educationRoutes.course(examDetails.course?.id),
              separator: "-",
            },
            { label: examDetails.title },
          ]}
        />
      )}

      <Box
        className="global__box__content mb-4 py-4"
        style={{ padding: "15px 2rem" }}
      >
        <Box
          className="d-flex justify-content-between align-items-center mb-4"
          style={{ gap: "1.9rem" }}
        >
          <Box className="info">
            {loading ? (
              <Skeleton variant="text" height={20} width={350} />
            ) : (
              <Typography variant="h1">{examDetails.title}</Typography>
            )}
            {loading ? (
              <Skeleton variant="text" height={15} width={150} />
            ) : (
              <Typography
                variant="body1"
                color="textSecondary"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  margin: "0.5rem 0",
                }}
              >
                <img src={icons.CALENDER} alt="calender" />{" "}
                {formatDate(examDetails.createdAt, "dddd, DD MMM, HH:mm Z")}
              </Typography>
            )}
          </Box>
        </Box>
        {loading ? (
          <>
            <Skeleton variant="text" width="75%" height={18} className="mb-2" />
            <Skeleton variant="text" width="50%" height={18} className="mb-2" />
            <Skeleton variant="text" width="65%" height={18} className="mb-2" />
            <Skeleton variant="text" width="40%" height={18} className="mb-2" />
            <Skeleton variant="text" width="30%" height={18} className="mb-2" />
          </>
        ) : (
          <Typography variant="body1">{examDetails.description}</Typography>
        )}
      </Box>

      {loading ? (
        <Skeleton
          variant="rect"
          height={200}
          className="mb-4"
          style={{ borderRadius: 8 }}
        />
      ) : showResult ? (
        <CheckpointResultComponent
          score={userResult.score ?? 0}
          percentage={userResult.percentage ?? 0}
          passingScore={
            userResult.passingScore ?? examDetails.passingScore ?? 0
          }
          totalQuestions={
            userResult.totalQuestions ?? examDetails.questions?.length ?? 0
          }
          result={userResult.result ?? "FAILED"}
          onRepeat={handleRepeat}
        />
      ) : (
        <ExamQuestionsComponent
          key={`exam-${examId}-${examRetakeKey}`}
          loading={loading}
          questions={examDetails.questions ?? []}
          examId={examId}
          checkpoint={examDetails}
          onExamComplete={handleExamComplete}
        />
      )}
    </Box>
  );
};

export default CheckpointExamContentComponent;
